/**
 * Situație pe canal × produs pentru dashboard „Producție & Obiective".
 * Obiectiv vs Realizat (după data CSD) pe fiecare canal și fiecare categorie.
 * HVAS în puncte; restul în nr. contracte.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Loader2, Target, ExternalLink } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const YEARS = [2024, 2025, 2026, 2027];
const MODES = [
  { key: "realized", label: "Realizat" },
  { key: "objective", label: "Obiectiv" },
  { key: "percent", label: "%" },
];

// Iter62i — selector de perioadă (lună/trimestru/an)
const PERIODS = [
  { key: "month", label: "Luna curentă" },
  { key: "quarter", label: "Trimestrul" },
  { key: "year", label: "Tot anul" },
];
// Iter62i — bază temporală
const BASES = [
  { key: "csd", label: "Data CSD" },
  { key: "signed", label: "Data încărcării" },
];

function periodToMonths(periodKey) {
  const now = new Date();
  const m = now.getMonth() + 1;
  if (periodKey === "month") return { mstart: m, mend: m };
  if (periodKey === "quarter") {
    const q = Math.floor((m - 1) / 3);
    return { mstart: 3 * q + 1, mend: 3 * q + 3 };
  }
  return { mstart: 1, mend: 12 };
}

const pctColor = (pct) => {
  if (pct == null) return "bg-slate-100 text-slate-400";
  if (pct >= 100) return "bg-emerald-100 text-emerald-800";
  if (pct >= 75) return "bg-lime-100 text-lime-800";
  if (pct >= 50) return "bg-amber-100 text-amber-800";
  if (pct > 0) return "bg-orange-100 text-orange-800";
  return "bg-red-50 text-red-500";
};

export default function ChannelProductMatrix() {
  const [year, setYear] = useState(2026);
  const [mode, setMode] = useState("realized");
  const [period, setPeriod] = useState("month");
  const [basis, setBasis] = useState("csd");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { mstart, mend } = periodToMonths(period);
      const r = await axios.get(`${API_URL}/channel-objectives/summary`, {
        params: { year, mstart, mend, date_basis: basis },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Nu am putut încărca situația pe canal & produs");
    } finally {
      setLoading(false);
    }
  }, [year, period, basis]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const computeCell = (ch, cat) => {
    const obj = data?.objective[ch]?.[cat] || 0;
    const real = data?.realized[ch]?.[cat] || 0;
    const hasObj = obj > 0;
    const pct = hasObj ? Math.round((real / obj) * 100) : null;
    let display;
    if (mode === "objective") display = obj.toLocaleString("ro-RO");
    else if (mode === "realized") display = real.toLocaleString("ro-RO");
    else display = hasObj ? pct + "%" : (real > 0 ? "n/a" : "—");
    return { obj, real, hasObj, pct, display };
  };

  const categories = data?.categories || [];
  const channels = data?.channels || [];

  return (
    <Card data-testid="channel-product-matrix">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Target className="h-5 w-5 text-indigo-700" />
            </div>
            <div>
              <CardTitle className="text-lg">Situație pe canal &amp; produs</CardTitle>
              <CardDescription>
                Obiectiv vs realizat pe fiecare canal și categorie · măsurat după{" "}
                {basis === "csd" ? "data CSD" : "data încărcării (vânzării)"} · HVAS în puncte ·
                conform manual PPC: EE/GAZ includ anulate după CSD, Transfer &amp; Product Change excluse
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-9 w-28" data-testid="cpm-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5" data-testid="cpm-period-toggle">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${period === p.key ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`cpm-period-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5" data-testid="cpm-basis-toggle">
              {BASES.map((b) => (
                <button
                  key={b.key}
                  onClick={() => setBasis(b.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${basis === b.key ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`cpm-basis-${b.key}`}
                >
                  {b.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5" data-testid="cpm-mode-toggle">
              {MODES.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMode(m.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${mode === m.key ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`cpm-mode-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-slate-500 border-b bg-slate-50">
                  <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-slate-50">Canal</th>
                  {categories.map((cat) => (
                    <th key={cat.key} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                      {cat.label}
                      {cat.unit === "puncte" && <span className="block text-[10px] font-normal text-slate-400">puncte</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {channels.map((ch) => {
                  const unmapped = data.hvas_unmapped?.[ch.key] || 0;
                  return (
                    <tr key={ch.key} className="border-b hover:bg-slate-50/60" data-testid={`cpm-row-${ch.key}`}>
                      <td className="px-3 py-2 font-medium text-slate-800 sticky left-0 bg-white">
                        {ch.label}
                        {mode !== "objective" && unmapped > 0 && (
                          <span className="ml-1.5 text-[10px] text-amber-600" title={`${unmapped} contracte HVAS fără punctaj mapat`}>
                            ⚠{unmapped}
                          </span>
                        )}
                      </td>
                      {categories.map((cat) => {
                        const { obj, pct, hasObj, display } = computeCell(ch.key, cat.key);
                        const canc = data?.cancelled?.[ch.key]?.[cat.key] || 0;
                        return (
                          <td key={cat.key} className="px-3 py-2 text-center" data-testid={`cpm-cell-${ch.key}-${cat.key}`}>
                            {mode === "percent" ? (
                              <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${pctColor(pct)}`}
                                    title={hasObj ? undefined : "Obiectiv nesetat pentru acest an"}>
                                {display}
                              </span>
                            ) : (
                              <span className={mode === "realized" ? "font-semibold text-slate-800" : "text-slate-600"}>
                                {display}
                              </span>
                            )}
                            {mode === "realized" && hasObj && (
                              <span className="block text-[10px] text-slate-400">/ {obj.toLocaleString("ro-RO")}</span>
                            )}
                            {mode === "realized" && canc > 0 && (
                              <span className="block text-[10px] text-rose-500" title="Contracte anulate după CSD — incluse în realizat conform regulii PPC, dar necomisionate">
                                din care {canc} anulate
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {mode !== "objective" && data?.unallocated && Object.values(data.unallocated).some((v) => v > 0) && (
                  <tr className="border-b bg-amber-50/40" data-testid="cpm-row-unallocated">
                    <td className="px-3 py-2 font-medium text-amber-700 sticky left-0 bg-amber-50/40"
                        title="Vânzări cu CSD în anul selectat, dar fără canal: agentul nu are agenție asociată sau vânzarea nu are agent/agenție. Nu intră în realizatul niciunui canal.">
                      Fără canal (nealocat)
                      {data.unallocated_hvas_unmapped > 0 && (
                        <span className="ml-1.5 text-[10px] text-amber-600" title={`${data.unallocated_hvas_unmapped} contracte HVAS nealocate fără punctaj mapat`}>
                          ⚠{data.unallocated_hvas_unmapped}
                        </span>
                      )}
                    </td>
                    {categories.map((cat) => (
                      <td key={cat.key} className="px-3 py-2 text-center text-amber-700" data-testid={`cpm-cell-unallocated-${cat.key}`}>
                        {(data.unallocated[cat.key] || 0).toLocaleString("ro-RO")}
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 flex justify-end">
          <Button asChild variant="ghost" size="sm" className="text-indigo-600" data-testid="cpm-open-objectives">
            <Link to="/objectives">Deschide matricea completă <ExternalLink className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
